// Analytics (ADR-0045). Two clearly separated sources:
//   Campañas        — numbers of the InteractAds Ad Server (accepted impressions, deduplicated events)
//   Demostraciones  — views and interactions of shareable demo links: TEST DATA, never added to the above.
// The browser renders what the server computed; it recomputes nothing but chart geometry.
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { BarChart3, FlaskConical, Info, RefreshCw } from "lucide-react";
import { useId, useMemo, useState, type ReactNode } from "react";
import { api, call } from "@/api/client";
import type { AnalyticsMetrics, AnalyticsReport, DemoAnalyticsReport } from "@/api/types";
import { Badge } from "@/ui/badge";
import { Button } from "@/ui/button";
import { Input, NativeSelect } from "@/ui/input";
import { Callout, EmptyState, PageHeader, Section, Skeleton, Table, Td, Th } from "@/ui/layout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/ui/tabs";
import { PageError, useRouteParams, useTitle } from "../common/page";
import { BarList, DailyChart, fillDays } from "./charts";
import {
  browserOffsetMinutes,
  formatDecimal,
  formatNumber,
  formatRate,
  formatSeconds,
  localDay,
  interactionTypeLabel,
} from "./format";

type Filters = {
  clientId: string;
  brandId: string;
  campaignId: string;
  creativeId: string;
  versionId: string;
  platform: string;
  format: string;
};
const NO_FILTERS: Filters = {
  clientId: "",
  brandId: "",
  campaignId: "",
  creativeId: "",
  versionId: "",
  platform: "",
  format: "",
};
const PERIODS = [
  { id: "7", label: "Últimos 7 días", days: 7 },
  { id: "30", label: "Últimos 30 días", days: 30 },
  { id: "90", label: "Últimos 90 días", days: 90 },
  { id: "custom", label: "Personalizado", days: 0 },
] as const;

function initialFilters(): Filters {
  const q = new URLSearchParams(window.location.search);
  return { ...NO_FILTERS, creativeId: q.get("creativeId") ?? "", campaignId: q.get("campaignId") ?? "" };
}

export function AnalyticsPage() {
  const { orgId } = useRouteParams("orgId");
  useTitle("Analítica");
  const [tab, setTab] = useState<"ads" | "demos">(() =>
    new URLSearchParams(window.location.search).get("tab") === "demos" ? "demos" : "ads"
  );
  return (
    <div className="grid gap-6">
      <PageHeader
        title="Analítica"
        description="Resultados de tus anuncios interactivos medidos por el Ad Server de InteractAds."
      />
      <Tabs
        value={tab}
        onValueChange={(v) => {
          setTab(v === "demos" ? "demos" : "ads");
        }}
      >
        <TabsList aria-label="Fuente de los datos">
          <TabsTrigger value="ads">Campañas</TabsTrigger>
          <TabsTrigger value="demos">
            Demostraciones <span className="ml-1 text-2xs text-subtle">(prueba)</span>
          </TabsTrigger>
        </TabsList>
        <TabsContent value="ads">
          <CampaignAnalytics orgId={orgId} />
        </TabsContent>
        <TabsContent value="demos">
          <DemoAnalytics orgId={orgId} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ---------------------------------------------------------------- campaigns (ad server)

function CampaignAnalytics({ orgId }: { orgId: string }) {
  const [period, setPeriod] = useState<(typeof PERIODS)[number]["id"]>("30");
  const [custom, setCustom] = useState({ from: localDay(29), to: localDay(0) });
  const [filters, setFilters] = useState<Filters>(initialFilters);
  const range = useMemo(() => {
    const p = PERIODS.find((x) => x.id === period);
    return p && p.days > 0 ? { from: localDay(p.days - 1), to: localDay(0) } : custom;
  }, [period, custom]);
  const query = {
    from: range.from,
    to: range.to,
    tz: browserOffsetMinutes(),
    ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== "")),
  };
  const report = useQuery({
    queryKey: ["analytics", orgId, query],
    queryFn: () =>
      call(() =>
        api.GET("/orgs/{orgId}/analytics/report", { params: { path: { orgId }, query: query as never } })
      ) as Promise<AnalyticsReport>,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
  const set = (key: keyof Filters, value: string) => {
    setFilters((f) => ({
      ...f,
      [key]: value,
      // A different creative has other versions.
      ...(key === "creativeId" ? { versionId: "" } : {}),
    }));
  };
  const data = report.data;

  return (
    <div className="grid gap-6">
      <div className="grid gap-3 rounded-md border border-line bg-surface p-3" role="search" aria-label="Filtros">
        <div className="grid grid-cols-[repeat(auto-fill,minmax(9.5rem,1fr))] gap-3">
          <FilterSelect
            label="Período"
            value={period}
            onChange={(v) => {
              setPeriod(v as typeof period);
            }}
          >
            {PERIODS.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </FilterSelect>
          {period === "custom" ? (
            <>
              <label className="grid gap-1 text-xs font-medium">
                Desde
                <Input
                  type="date"
                  value={custom.from}
                  max={custom.to}
                  onChange={(e) => {
                    setCustom((c) => ({ ...c, from: e.target.value || c.from }));
                  }}
                />
              </label>
              <label className="grid gap-1 text-xs font-medium">
                Hasta
                <Input
                  type="date"
                  value={custom.to}
                  min={custom.from}
                  max={localDay(0)}
                  onChange={(e) => {
                    setCustom((c) => ({ ...c, to: e.target.value || c.to }));
                  }}
                />
              </label>
            </>
          ) : null}
          <OptionSelect
            label="Cliente"
            value={filters.clientId}
            options={data?.options.clients}
            onChange={(v) => {
              set("clientId", v);
            }}
            all="Todos"
          />
          <OptionSelect
            label="Marca"
            value={filters.brandId}
            options={data?.options.brands}
            onChange={(v) => {
              set("brandId", v);
            }}
            all="Todas"
          />
          <OptionSelect
            label="Campaña"
            value={filters.campaignId}
            options={data?.options.campaigns}
            onChange={(v) => {
              set("campaignId", v);
            }}
            all="Todas"
          />
          <OptionSelect
            label="Creativo"
            value={filters.creativeId}
            options={data?.options.creatives}
            onChange={(v) => {
              set("creativeId", v);
            }}
            all="Todos"
          />
          {filters.creativeId ? (
            <OptionSelect
              label="Versión"
              value={filters.versionId}
              options={(data?.byVersion ?? [])
                .filter((v) => v.creativeId === filters.creativeId || v.id === filters.versionId)
                .map((v) => ({
                  id: v.id,
                  name: v.versionNumber === null ? "Versión" : `Versión ${String(v.versionNumber)}`,
                }))}
              onChange={(v) => {
                set("versionId", v);
              }}
              all="Todas"
            />
          ) : null}
          <OptionSelect
            label="Plataforma"
            value={filters.platform}
            options={data?.options.platforms.map((p) => ({ id: p.key, name: p.name }))}
            onChange={(v) => {
              set("platform", v);
            }}
            all="Todas"
          />
          <OptionSelect
            label="Formato"
            value={filters.format}
            options={data?.options.formats.map((f) => ({ id: f, name: f.replace("x", " × ") }))}
            onChange={(v) => {
              set("format", v);
            }}
            all="Todos"
          />
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted">
          <span>
            {data ? (
              <>
                Del {data.period.from} al {data.period.to} · hora local · {formatNumber(data.source.distributions)}{" "}
                {data.source.distributions === 1 ? "Distribution" : "Distributions"} · Fuente: Ad Server de InteractAds
              </>
            ) : (
              "Cargando…"
            )}
          </span>
          <div className="flex items-center gap-2">
            {Object.values(filters).some((v) => v !== "") ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setFilters(NO_FILTERS);
                }}
              >
                Quitar filtros
              </Button>
            ) : null}
            <Button variant="ghost" size="sm" onClick={() => void report.refetch()} disabled={report.isFetching}>
              <RefreshCw className={report.isFetching ? "animate-spin" : ""} /> Actualizar
            </Button>
          </div>
        </div>
      </div>

      {report.error && !data ? <PageError error={report.error} orgId={orgId} /> : null}
      {report.isLoading ? <ReportSkeleton /> : null}
      {data ? <Report data={data} orgId={orgId} stale={report.isFetching} /> : null}
    </div>
  );
}

function Report({ data, orgId, stale }: { data: AnalyticsReport; orgId: string; stale: boolean }) {
  const t = data.totals;
  if (data.source.distributions === 0)
    return (
      <EmptyState
        icon={<BarChart3 />}
        title="Todavía no hay anuncios para analizar"
        description="Cuando publiques un creativo en una Distribution y su Tag empiece a servir, aquí verás sus resultados."
        action={
          <Button asChild>
            <Link to={`/o/${orgId}/creatives`}>Ir a Creatividades</Link>
          </Button>
        }
      />
    );
  const noTraffic = t.served === 0 && t.impressions === 0;
  return (
    <div className={`grid gap-8 transition-opacity ${stale ? "opacity-70" : ""}`} aria-busy={stale}>
      {data.source.truncated ? (
        <Callout tone="warning" title="Reporte parcial">
          Hay más de 200 Distributions con estos filtros: el reporte incluye las 200 más recientes. Filtra por campaña o
          creativo para ver el resto.
        </Callout>
      ) : null}
      {noTraffic ? (
        <Callout tone="info" title="Sin tráfico en este período">
          El Ad Server no registró anuncios servidos para estos filtros. Prueba con un período más amplio.
        </Callout>
      ) : null}

      <section aria-label="Indicadores principales" className="grid gap-3">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-7">
          <Kpi
            label="Impresiones"
            value={formatNumber(t.impressions)}
            hint="Anuncios que se presentaron y fueron aceptados por el Ad Server (una por instancia)."
            primary
          />
          <Kpi label="Clics" value={formatNumber(t.clicks)} hint="Clics hacia la página del anunciante (cta_click)." />
          <Kpi label="CTR" value={formatRate(t.ctr)} hint="Clics ÷ impresiones." />
          <Kpi
            label="Engagements"
            value={formatNumber(t.interactions)}
            hint="Total de gestos de las personas: toques, respuestas, controles y clics de salida (cada clic una vez). Los toques de juego se suman por ventana."
          />
          <Kpi
            label="Engagement Rate"
            value={formatRate(t.engagementRate)}
            hint="Engagements ÷ impresiones. Mide intensidad: puede superar el 100 %."
          />
          <Kpi
            label="Instancias con participación"
            value={formatNumber(t.engaged)}
            hint="Cada carga del anuncio es una instancia. No identificamos personas: no hay usuarios únicos."
          />
          <Kpi
            label="Tasa de participación"
            value={formatRate(t.interactionRate)}
            hint="Instancias con participación ÷ impresiones. Nunca supera el 100 %."
          />
        </div>
        <dl className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted">
          <MiniStat label="Servidas" value={formatNumber(t.served)} />
          <MiniStat label="Interactivas" value={formatNumber(t.interactive)} />
          <MiniStat label="Respaldo" value={formatNumber(t.fallback)} />
          <MiniStat label="Visibles (criterio provisional)" value={formatNumber(t.viewable)} />
          <MiniStat label="Instancias con clic" value={formatNumber(t.clickers)} />
          <MiniStat label="Revisiones de plataformas (aparte)" value={formatNumber(t.reviewImpressions)} />
        </dl>
        {t.reviewImpressions > 0 ? (
          <p className="text-xs text-muted" data-testid="analytics-review-note">
            Además, {formatNumber(t.reviewImpressions)} cargas fueron revisiones automáticas de creativos de una
            plataforma, confirmadas por su red de auditoría. No son impresiones facturables y no están en estos totales.
          </p>
        ) : null}
        <p className="text-xs text-muted" data-testid="analytics-engagement-note">
          Engagements usa los eventos que el Ad Server ya registró, sin verificar que cada uno venga de un gesto real.
          En anuncios HTML5 cuenta lo que emite cada anuncio; en los del Studio puede incluir pasadas del cursor,
          choques de juego y cambios de carrusel disparados por reglas.
        </p>
      </section>

      <Section title="Evolución diaria" description="Cada anuncio cuenta en el día local en que se sirvió.">
        <div className="grid gap-3 md:grid-cols-3">
          <DailyChart
            title="Impresiones"
            total={t.impressions}
            points={fillDays(
              data.period.from,
              data.period.to,
              data.daily.map((d) => ({ day: d.day, value: d.impressions }))
            )}
          />
          <DailyChart
            title="Clics"
            total={t.clicks}
            points={fillDays(
              data.period.from,
              data.period.to,
              data.daily.map((d) => ({ day: d.day, value: d.clicks }))
            )}
          />
          <DailyChart
            title="Engagements"
            total={t.interactions}
            points={fillDays(
              data.period.from,
              data.period.to,
              data.daily.map((d) => ({ day: d.day, value: d.interactions }))
            )}
          />
        </div>
      </Section>

      <div className="grid items-start gap-6 lg:grid-cols-3">
        {/* min-w-0: a long creative name truncates inside its column instead of covering the next one. */}
        <Section
          title="Comparación entre creativos"
          description="Impresiones · CTR · Engagement Rate"
          className="min-w-0 lg:col-span-1"
        >
          <BarList
            label="Impresiones por creativo"
            data={data.byCreative.map((c) => ({
              key: c.id,
              label: c.name,
              value: c.metrics.impressions,
              display: `${formatNumber(c.metrics.impressions)} · ${formatRate(c.metrics.ctr)} · ${formatRate(c.metrics.engagementRate)}`,
            }))}
          />
        </Section>
        <Section title="Por plataforma" description="Impresiones · CTR" className="min-w-0">
          <BarList
            label="Impresiones por plataforma"
            data={data.byPlatform.map((p) => ({
              key: p.key,
              label: p.name,
              value: p.metrics.impressions,
              display: `${formatNumber(p.metrics.impressions)} · ${formatRate(p.metrics.ctr)}`,
            }))}
          />
        </Section>
        <Section title="Por formato" description="Impresiones · CTR" className="min-w-0">
          <BarList
            label="Impresiones por formato"
            data={data.byFormat.map((f) => ({
              key: f.key,
              label: f.key.replace("x", " × "),
              value: f.metrics.impressions,
              display: `${formatNumber(f.metrics.impressions)} · ${formatRate(f.metrics.ctr)}`,
            }))}
          />
        </Section>
      </div>

      {data.games ? <GamesSection games={data.games} /> : null}
      {data.video ? <VideoSection video={data.video} /> : null}
      {data.surveys.length > 0 ? <SurveysSection surveys={data.surveys} /> : null}
      <Section
        title="Engagements por tipo de evento"
        description="Suma exactamente el total de Engagements. Cada clic de salida cuenta una vez."
      >
        <BarList
          label="Engagements por tipo de evento"
          data={data.interactionsByType.map((x) => ({
            key: x.type,
            label: interactionTypeLabel(x.type),
            value: x.count,
            display: formatNumber(x.count),
          }))}
        />
      </Section>

      {data.ctaLinks.length > 0 ? (
        <Section title="Clics en botones y productos" description="Clics por destino declarado en el creativo.">
          <Table>
            <thead>
              <tr>
                <Th>Creativo</Th>
                <Th>Botón / enlace</Th>
                <Th>Destino</Th>
                <Th className="text-right">Clics</Th>
              </tr>
            </thead>
            <tbody>
              {data.ctaLinks.map((l) => (
                <tr key={`${l.creative.id}|${l.linkId}`}>
                  <Td>{l.creative.name}</Td>
                  <Td>{l.label ?? l.linkId}</Td>
                  <Td className="text-muted">{l.host ?? "—"}</Td>
                  <Td className="text-right tabular-nums">{formatNumber(l.clicks)}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Section>
      ) : null}

      <Section title="Detalle por creativo">
        <MetricsTable
          rows={data.byCreative.map((c) => ({ key: c.id, name: c.name, sub: c.campaign.name, m: c.metrics }))}
          first="Creativo"
        />
      </Section>
      <Section
        title="Detalle por Distribution"
        description="Cada Distribution es un Tag permanente en una plataforma y formato."
      >
        <MetricsTable
          rows={data.byDistribution.map((d) => ({
            key: d.id,
            name: d.name,
            sub: `${d.creative.name} · ${d.platformName} · ${d.format}${d.archived ? " · archivada" : ""}`,
            m: d.metrics,
            href: `/o/${orgId}/distributions/${d.id}`,
          }))}
          first="Distribution"
        />
      </Section>
      {data.byCampaign.length > 1 ? (
        <Section title="Detalle por campaña">
          <MetricsTable
            rows={data.byCampaign.map((c) => ({ key: c.id, name: c.name, m: c.metrics }))}
            first="Campaña"
          />
        </Section>
      ) : null}

      <Methodology />
    </div>
  );
}

function GamesSection({ games }: { games: NonNullable<AnalyticsReport["games"]> }) {
  const outcomeLabel: Record<string, string> = {
    win: "Ganadas",
    lose: "Perdidas",
    timeout: "Tiempo agotado",
    neutral: "Sin resultado",
  };
  const totalOutcomes = games.outcomes.reduce((s, o) => s + o.n, 0);
  return (
    <Section title="Juegos" description="Participación, finalización y puntajes de las partidas.">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-7">
        <Kpi label="Partidas iniciadas" value={formatNumber(games.starts)} />
        <Kpi label="Partidas terminadas" value={formatNumber(games.completes)} />
        <Kpi
          label="Tasa de finalización"
          value={formatRate(games.completionRate)}
          hint="Partidas terminadas ÷ partidas iniciadas."
        />
        <Kpi label="Reinicios" value={formatNumber(games.restarts)} />
        <Kpi
          label="Participación en juegos"
          value={formatRate(games.playerRate)}
          hint="Instancias con al menos una partida ÷ impresiones."
        />
        <Kpi
          label="Duración media"
          value={formatSeconds(games.averageDurationSeconds)}
          hint="Desde el inicio hasta el final de cada partida terminada."
        />
        <Kpi
          label="Puntaje medio"
          value={formatDecimal(games.scores?.average)}
          hint={
            games.scores
              ? `Mín. ${String(games.scores.min)} · máx. ${String(games.scores.max)} · ${formatNumber(games.scores.count)} partidas con puntaje`
              : "El juego no reporta puntaje."
          }
        />
      </div>
      <div className="grid gap-6 md:grid-cols-2">
        <div className="grid gap-2">
          <h3 className="text-xs font-medium text-muted">Resultados</h3>
          <BarList
            label="Resultados de las partidas"
            data={games.outcomes.map((o) => ({
              key: o.value,
              label: outcomeLabel[o.value] ?? o.value,
              value: o.n,
              display: `${formatNumber(o.n)} · ${formatRate(totalOutcomes > 0 ? o.n / totalOutcomes : null)}`,
            }))}
          />
        </div>
        <div className="grid gap-2">
          <h3 className="text-xs font-medium text-muted">Distribución de puntajes</h3>
          <BarList
            label="Partidas por puntaje"
            empty="El juego no reporta puntaje."
            data={(games.scores?.distribution ?? []).map((s) => ({
              key: s.value,
              label: s.value === "__other__" ? "Otros puntajes" : `${s.value} puntos`,
              value: s.n,
            }))}
          />
        </div>
      </div>
      <p className="text-xs text-muted">
        Interacciones dentro de los juegos: {formatNumber(games.interactions)} toques o arrastres. Instancias que
        jugaron: {formatNumber(games.players)} · que terminaron al menos una partida: {formatNumber(games.completers)}.
      </p>
    </Section>
  );
}

function VideoSection({ video }: { video: NonNullable<AnalyticsReport["video"]> }) {
  const steps = [
    { key: "start", label: "Inicio", value: video.starts },
    { key: "25", label: "25 %", value: video.q25 },
    { key: "50", label: "50 %", value: video.q50 },
    { key: "75", label: "75 %", value: video.q75 },
    { key: "complete", label: "Completo", value: video.completes },
  ];
  return (
    <Section
      title="Video"
      description={`Reproducciones y avance. Tasa de finalización: ${formatRate(video.completionRate)}.`}
    >
      <BarList
        label="Avance de reproducción"
        max={video.starts}
        data={steps.map((s) => ({
          ...s,
          display: `${formatNumber(s.value)} · ${formatRate(video.starts > 0 ? s.value / video.starts : null)}`,
        }))}
      />
    </Section>
  );
}

function SurveysSection({ surveys }: { surveys: AnalyticsReport["surveys"] }) {
  return (
    <Section
      title="Encuestas"
      description="Respuestas por pregunta. Muestra no probabilística: los porcentajes describen a quienes respondieron, no a una población."
    >
      {surveys.map((s) => (
        <div key={s.versionId} className="grid gap-4 rounded-md border border-line bg-surface p-4">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h3 className="text-sm font-semibold">
              {s.creative.name}
              {s.versionNumber !== null ? (
                <span className="font-normal text-muted"> · versión {s.versionNumber}</span>
              ) : null}
            </h3>
            <span className="text-xs text-muted">
              {formatNumber(s.starts)} iniciadas · {formatNumber(s.completes)} completas ·{" "}
              {formatRate(s.completionRate)}
            </span>
          </div>
          <div className="grid gap-6 md:grid-cols-2">
            {s.questions.map((q) => (
              <div key={q.id} className="grid gap-2">
                <p className="text-sm font-medium">{q.prompt}</p>
                <p className="text-xs text-muted">
                  {formatNumber(q.responses)} respuestas{q.views > 0 ? ` · ${formatNumber(q.views)} vistas` : ""}
                </p>
                <BarList
                  label={`Respuestas: ${q.prompt}`}
                  data={q.options.map((o) => ({
                    key: o.id,
                    label: o.label,
                    value: o.n,
                    display: `${formatNumber(o.n)} · ${formatRate(o.share)}`,
                  }))}
                />
              </div>
            ))}
          </div>
        </div>
      ))}
    </Section>
  );
}

function MetricsTable({
  rows,
  first,
}: {
  rows: Array<{ key: string; name: string; sub?: string; m: AnalyticsMetrics; href?: string }>;
  first: string;
}) {
  if (rows.length === 0) return <p className="text-sm text-muted">Sin datos en el período.</p>;
  return (
    <Table>
      <thead>
        <tr>
          <Th>{first}</Th>
          <Th className="text-right">Servidas</Th>
          <Th className="text-right">Impresiones</Th>
          <Th className="text-right">Clics</Th>
          <Th className="text-right">CTR</Th>
          <Th className="text-right">Engagements</Th>
          <Th className="text-right">Engagement Rate</Th>
          <Th className="text-right">Tasa de participación</Th>
          <Th className="text-right">Partidas</Th>
          <Th className="text-right">Finalización</Th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.key}>
            <Td>
              <div className="grid min-w-40">
                {r.href ? (
                  <Link to={r.href} className="font-medium hover:underline">
                    {r.name}
                  </Link>
                ) : (
                  <span className="font-medium">{r.name}</span>
                )}
                {r.sub ? <span className="text-xs text-muted">{r.sub}</span> : null}
              </div>
            </Td>
            <Td className="text-right tabular-nums">{formatNumber(r.m.served)}</Td>
            <Td className="text-right tabular-nums">{formatNumber(r.m.impressions)}</Td>
            <Td className="text-right tabular-nums">{formatNumber(r.m.clicks)}</Td>
            <Td className="text-right tabular-nums">{formatRate(r.m.ctr)}</Td>
            <Td className="text-right tabular-nums">{formatNumber(r.m.interactions)}</Td>
            <Td className="text-right tabular-nums">{formatRate(r.m.engagementRate)}</Td>
            <Td className="text-right tabular-nums">{formatRate(r.m.interactionRate)}</Td>
            <Td className="text-right tabular-nums">{formatNumber(r.m.gameStarts)}</Td>
            <Td className="text-right tabular-nums">{formatRate(r.m.gameCompletionRate)}</Td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

function Methodology() {
  return (
    <details className="rounded-md border border-line bg-surface p-3 text-sm">
      <summary className="flex cursor-pointer items-center gap-2 font-medium">
        <Info className="size-4 text-muted" /> Cómo se calculan estos indicadores
      </summary>
      <dl className="mt-3 grid gap-2 text-muted sm:grid-cols-2">
        <Def term="Impresión">
          El anuncio se presentó y el Ad Server lo aceptó: una sola vez por instancia, aunque el navegador reintente.
        </Def>
        <Def term="Servida">
          El Ad Server decidió entregar el anuncio. Puede no llegar a presentarse (página cerrada, error de carga).
        </Def>
        <Def term="Clic">Apertura de la página del anunciante desde un botón, elemento o zona del anuncio.</Def>
        <Def term="CTR">Clics ÷ impresiones.</Def>
        <Def term="Engagements">
          Total de gestos: toque, arrastre, respuesta, control de video, navegación de carrusel o clic de salida. Cada
          clic cuenta una vez. En juegos, los toques se agrupan en ventanas de 5 s y se suman.
        </Def>
        <Def term="Engagement Rate">Engagements ÷ impresiones. Mide intensidad: puede superar el 100 %.</Def>
        <Def term="Instancias con participación">
          Cargas del anuncio con al menos un gesto. Cada una cuenta una vez.
        </Def>
        <Def term="Tasa de participación">Instancias con participación ÷ impresiones.</Def>
        <Def term="Sin impresiones">Una tasa sin denominador se muestra como N/D, nunca como 0 %.</Def>
        <Def term="Limitación actual">
          Se usan los eventos ya registrados y no se recalcula la historia: no hay verificación de que cada evento venga
          de un gesto real.
        </Def>
        <Def term="Tasa de finalización (juegos)">Partidas terminadas ÷ partidas iniciadas.</Def>
        <Def term="Tasa de finalización (video)">Reproducciones completas ÷ reproducciones iniciadas.</Def>
        <Def term="Visibles">
          Instancias que informaron visibilidad con un criterio provisional (no acreditado). No condiciona la
          facturación.
        </Def>
        <Def term="Usuarios únicos">
          No se ofrecen: InteractAds no identifica personas ni dispositivos. Cada carga del anuncio es una instancia.
        </Def>
      </dl>
      <p className="mt-3 text-xs text-muted">
        Fuente: registros del Ad Server de InteractAds. No incluye las métricas de tu plataforma publicitaria (gasto,
        CPM), que pueden diferir. Las demostraciones nunca se incluyen.
      </p>
    </details>
  );
}

// ---------------------------------------------------------------- demos (test data)

function DemoAnalytics({ orgId }: { orgId: string }) {
  const [creativeId, setCreativeId] = useState("");
  const demos = useQuery({
    queryKey: ["analytics-demos", orgId, creativeId],
    queryFn: () =>
      call(() =>
        api.GET("/orgs/{orgId}/analytics/demos", {
          params: { path: { orgId }, query: creativeId ? { creativeId } : {} },
        })
      ) as Promise<DemoAnalyticsReport>,
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  });
  const data = demos.data;
  const creatives = useMemo(() => {
    const m = new Map<string, string>();
    for (const l of data?.links ?? []) m.set(l.creative.id, l.creative.name);
    return [...m.entries()].map(([id, name]) => ({ id, name }));
  }, [data]);
  const statusTone = { ACTIVE: "success", EXPIRED: "neutral", REVOKED: "danger" } as const;
  const statusLabel = { ACTIVE: "Activo", EXPIRED: "Vencido", REVOKED: "Revocado" } as const;
  return (
    <div className="grid gap-6">
      <Callout tone="warning" title="Datos de prueba">
        <span className="inline-flex items-start gap-2">
          <FlaskConical className="mt-0.5 size-4 shrink-0" />
          Estas cifras vienen de los enlaces de demostración. No son impresiones, clics ni interacciones de campañas y
          nunca se suman a ellas.
        </span>
      </Callout>
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-64">
          <OptionSelect label="Creativo" value={creativeId} options={creatives} onChange={setCreativeId} all="Todos" />
        </div>
        <Button variant="ghost" size="sm" onClick={() => void demos.refetch()} disabled={demos.isFetching}>
          <RefreshCw className={demos.isFetching ? "animate-spin" : ""} /> Actualizar
        </Button>
      </div>
      {demos.error && !data ? <PageError error={demos.error} orgId={orgId} /> : null}
      {demos.isLoading ? <ReportSkeleton /> : null}
      {data && data.links.length === 0 ? (
        <EmptyState
          icon={<FlaskConical />}
          title="Aún no hay enlaces de demostración"
          description="Desde Creative Studio, «Compartir demostración» crea un enlace público para mostrar un creativo."
        />
      ) : null}
      {data && data.links.length > 0 ? (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
            <Kpi
              label="Enlaces"
              value={formatNumber(data.totals.links)}
              hint={`${formatNumber(data.totals.activeLinks)} activos`}
              test
            />
            <Kpi
              label="Visualizaciones"
              value={formatNumber(data.totals.presented)}
              hint="Espectadores cuyo anuncio se presentó (uno por navegador)."
              test
            />
            <Kpi label="Cargas de página" value={formatNumber(data.totals.pageLoads)} test />
            <Kpi label="Interacciones" value={formatNumber(data.totals.interactions)} test />
            <Kpi label="Clics" value={formatNumber(data.totals.clicks)} test />
            <Kpi
              label="Partidas (iniciadas / terminadas)"
              value={`${formatNumber(data.totals.gameStarts)} / ${formatNumber(data.totals.gameCompletes)}`}
              test
            />
          </div>
          <Table>
            <thead>
              <tr>
                <Th>Enlace</Th>
                <Th>Estado</Th>
                <Th className="text-right">Visualizaciones</Th>
                <Th className="text-right">Cargas</Th>
                <Th className="text-right">Interacciones</Th>
                <Th className="text-right">Clics</Th>
                <Th className="text-right">Partidas</Th>
                <Th className="text-right">Finalización</Th>
                <Th className="text-right">Puntaje medio</Th>
                <Th>Resultados</Th>
              </tr>
            </thead>
            <tbody>
              {data.links.map((l) => (
                <tr key={l.id}>
                  <Td>
                    <div className="grid min-w-44">
                      <span className="font-medium">
                        {l.creative.name} <span className="font-normal text-muted">· versión {l.versionNumber}</span>
                      </span>
                      <span className="text-xs text-muted">
                        {l.label ? `${l.label} · ` : ""}creado {new Date(l.createdAt).toLocaleDateString("es")}
                      </span>
                    </div>
                  </Td>
                  <Td>
                    <Badge tone={statusTone[l.status]}>{statusLabel[l.status]}</Badge>
                  </Td>
                  <Td className="text-right tabular-nums">{formatNumber(l.presented)}</Td>
                  <Td className="text-right tabular-nums">{formatNumber(l.pageLoads)}</Td>
                  <Td className="text-right tabular-nums">{formatNumber(l.interactions)}</Td>
                  <Td className="text-right tabular-nums">{formatNumber(l.clicks)}</Td>
                  <Td className="text-right tabular-nums">
                    {formatNumber(l.gameStarts)} / {formatNumber(l.gameCompletes)}
                  </Td>
                  <Td className="text-right tabular-nums">{formatRate(l.gameCompletionRate)}</Td>
                  <Td className="text-right tabular-nums">{formatDecimal(l.averageScore)}</Td>
                  <Td className="text-xs text-muted">
                    {l.outcomes.length > 0
                      ? l.outcomes
                          .map(
                            (o) =>
                              `${{ win: "ganadas", lose: "perdidas", timeout: "tiempo", neutral: "sin resultado" }[o.value] ?? o.value} ${String(o.n)}`
                          )
                          .join(" · ")
                      : "—"}
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
          <p className="text-xs text-muted">
            Las interacciones de demostración se registran desde la incorporación de esta medición; los enlaces
            anteriores solo tienen visualizaciones.
          </p>
        </>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------- pieces

export function Kpi({
  label,
  value,
  hint,
  primary,
  test,
}: {
  label: string;
  value: string;
  hint?: string;
  primary?: boolean;
  test?: boolean;
}) {
  return (
    <div className="grid content-start gap-1 rounded-md border border-line bg-surface p-3" title={hint}>
      <span className="flex items-center gap-1 text-xs text-muted">
        {label}
        {test ? <span className="rounded-sm bg-warning-soft px-1 text-2xs text-warning">prueba</span> : null}
      </span>
      <span className={`font-semibold tabular-nums tracking-tight ${primary ? "text-2xl text-brand" : "text-xl"}`}>
        {value}
      </span>
      {hint ? <span className="text-2xs leading-snug text-subtle">{hint}</span> : null}
    </div>
  );
}

function MiniStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-1">
      <dt>{label}:</dt>
      <dd className="font-medium tabular-nums text-fg">{value}</dd>
    </div>
  );
}

function Def({ term, children }: { term: string; children: ReactNode }) {
  return (
    <div>
      <dt className="font-medium text-fg">{term}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  children,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  children: ReactNode;
}) {
  const id = useId();
  return (
    <div className="grid content-start gap-1">
      <label htmlFor={id} className="text-xs font-medium">
        {label}
      </label>
      <NativeSelect
        id={id}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
        }}
      >
        {children}
      </NativeSelect>
    </div>
  );
}

function OptionSelect({
  label,
  value,
  options,
  onChange,
  all,
}: {
  label: string;
  value: string;
  options: ReadonlyArray<{ id: string; name: string }> | undefined;
  onChange: (v: string) => void;
  all: string;
}) {
  const list = options ?? [];
  return (
    <FilterSelect label={label} value={value} onChange={onChange}>
      <option value="">{all}</option>
      {value && !list.some((o) => o.id === value) ? <option value={value}>Seleccionado</option> : null}
      {list.map((o) => (
        <option key={o.id} value={o.id}>
          {o.name}
        </option>
      ))}
    </FilterSelect>
  );
}

function ReportSkeleton() {
  return (
    <div className="grid gap-4" aria-busy="true" aria-label="Cargando estadísticas">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-6">
        {Array.from({ length: 6 }, (_, i) => (
          <Skeleton key={i} className="h-20" />
        ))}
      </div>
      <Skeleton className="h-40" />
    </div>
  );
}
